import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';

import Grid from '@mui/material/Grid';
import MuiRating from '@mui/material/Rating';

import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { useWindowSize } from 'react-use';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import TextField from 'src/components/ui/TextField';
import Dialog from 'src/components/ui/Dialog';
import Icon from 'src/components/ui/Icon';
import Button from 'src/components/ui/Button';
import Typography from 'src/components/ui/Typography';
import Avatar from 'src/components/ui/Avatar';

import theme from 'src/utils/theme';
import useAlerts from 'src/hooks/useAlerts';
import RatingCard from 'src/components/profile/RatingCard';
import { colours } from 'src/constants/colours';
import { breakpoints } from 'src/constants/breakpoints';
import useUser from 'src/hooks/useUser';
import useLoginModal from 'src/hooks/useLoginModal';

import { useCreateRatingMutation, useUpdateRatingMutation, useRatingsQuery } from 'src/hooks/__generated__/queries';
import type { RatingsQuery, Medium, UserProfileQuery, CompanyProfileQuery } from 'src/types/__generated__/graphql';
import { useSsrWindowSize } from 'src/hooks/useSsr';
import { styledComponent } from 'src/utils/styled';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent } from 'src/utils/analytics';

const StyledCard = styledComponent(Card)`
    overflow: initial;
`;

const StyledCardContent = styledComponent(CardContent)`
    position: relative;
    padding: 1.5rem 0;
`;

const StyledRatingCard = styledComponent(Card)`
    margin: -1rem;
    height: calc(100% + 2rem);
`;
StyledRatingCard.defaultProps = {
    elevation: 0,
};

const StyledDialog = styledComponent(Dialog)`
    .MuiDialog-paper {
        min-width: 32rem;
    }
`;

StyledDialog.defaultProps = {
    fullWidth: true,
};

const CloseButton = styledComponent(IconButton)`
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
`;

const StyledAvatar = styledComponent(Avatar)`
    border: 0.125rem solid ${theme.palette.background.paper};
    background-color: ${theme.palette.primary.main};
    box-shadow: 0 0.25rem 0.625rem ${colours.greyOpacity.O50};
`;

const StyledRating = styledComponent(MuiRating)`
    color: ${colours.paletteRustMain};
    font-size: 2rem;
`;

const StyledDialogActions = styledComponent(DialogActions)`
    padding: 0.5rem 1.5rem 1.5rem 1.5rem;
`;

interface FormValues {
    rating?: number;
    comment?: string;
}

export interface Props {
    user?: UserProfileQuery['userProfile'];
    company?: CompanyProfileQuery['companyProfile'];
    openRecommendationModal?: () => void;
    refetchQueries?: string[];
}
const defaultTake = 4;

const RatingSection: React.FC<Props> = ({
    user,
    company,
    openRecommendationModal,
    refetchQueries = ['UserProfile', 'Ratings', 'CompanyProfile'],
}) => {
    const { logEventDynamic } = useDynamicLogEvents();
    const { t } = useTranslation(['profile', 'common']);
    const { addAlert } = useAlerts();
    const { width } = useSsrWindowSize();

    const currentUser = useUser();
    const { createAuthenticatedHandler } = useLoginModal();
    const ratingGivenByCurrentUser = user?.ratingGivenByCurrentUser ?? company?.ratingGivenByCurrentUser;

    const { data: ratings, fetchMore } = useRatingsQuery({
        variables: {
            userId: user?.id as string,
            companyId: company?.id as string,
            withHiddenRatings: false,
            skip: 0,
            take: ratingGivenByCurrentUser && !company ? 3 : defaultTake,
            includeCurrentUserRatings: Boolean(company?.id),
        },
        fetchPolicy: 'cache-and-network',
    });

    const [createRating] = useCreateRatingMutation();
    const [updateRating] = useUpdateRatingMutation();

    const [ratingModalOpened, setRatingModalOpened] = useState(false);

    const [hoveredRating, setHoveredRating] = useState<number>(-1);

    const handleRatingHover = (_event: React.SyntheticEvent, value: number) => {
        setHoveredRating(value);
    };

    const handleOpenRatingModal = () => {
        logEventDynamic(AnalyticsEvent.Rating);
        setRatingModalOpened(true);
    };

    const handleCloseRatingModal = () => {
        setRatingModalOpened(false);
    };

    const handleSubmit = async (values: FormValues) => {
        try {
            if (ratingGivenByCurrentUser) {
                await updateRating({
                    variables: {
                        id: ratingGivenByCurrentUser.id,
                        value: Number(values.rating),
                        comment: values.comment,
                    },
                    refetchQueries,
                    awaitRefetchQueries: true,
                });
            } else {
                await createRating({
                    variables: {
                        userId: user?.id as string,
                        value: Number(values.rating),
                        comment: values.comment,
                    },
                    refetchQueries,
                    awaitRefetchQueries: true,
                });
            }
            handleCloseRatingModal();
            addAlert({
                type: 'success',
                message: t('profile:createRatingSuccess'),
            });
        } catch (error: unknown) {
            addAlert({
                type: 'error',
                message: (error as Error).message,
            });
        }
        if ((values.rating ?? 0) >= 4) {
            openRecommendationModal?.();
        }
    };

    const schema = Yup.object().shape({
        comment: Yup.string().max(400, t('common:errors.tooLong', { number: 400 })),
    });

    const handleLoadMore = async () => {
        await fetchMore({
            variables: {
                skip: ratings?.ratings?.allRatings?.length ?? 0,
                take: defaultTake,
            },
            updateQuery(prev, { fetchMoreResult }: { fetchMoreResult?: RatingsQuery }) {
                if (!fetchMoreResult) return prev;
                const prevItems = prev.ratings?.allRatings ?? [];
                const newItems = fetchMoreResult.ratings?.allRatings ?? [];
                return {
                    ratings: {
                        ...prev.ratings,
                        allRatings: [...prevItems, ...newItems],
                    },
                };
            },
        });
    };

    // User's own ratings come from the ratingGivenByCurrentUser variables, put them "first" in the array
    const ratingsReceived = ratings?.ratings?.allRatings ?? [];
    const mergedRatings =
        ratingGivenByCurrentUser?.comment && !ratingGivenByCurrentUser.isHidden
            ? [ratingGivenByCurrentUser, ...ratingsReceived]
            : ratingsReceived;

    const isMobile = width !== undefined && width < breakpoints.tabletPortrait;
    const ratingsSummaryDataForHeader = {
        numberOfRatings: user?.numberOfRatings ?? company?.numberOfRatings ?? ratings?.ratings?.numberOfRatings ?? 0,
        averageRating: user?.averageRating ?? company?.averageRating ?? ratings?.ratings?.averageRating,
    };

    return (
        <StyledCard elevation={0}>
            <StyledCardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                        <Box mr={1}>
                            <Typography variant="h2" serif>
                                {t('profile:ratingsTitle')}
                            </Typography>
                        </Box>
                        <Icon color={theme.palette.tertiary.main} name="Star" />
                        <Box px={1} data-target-id="rating-section-average-rating">
                            <Typography>{ratingsSummaryDataForHeader.averageRating}</Typography>
                        </Box>
                        <Typography data-target-id="rating-section-number-ratings">
                            {isMobile
                                ? `(${ratingsSummaryDataForHeader.numberOfRatings})`
                                : t('profile:ratings', {
                                      count: ratingsSummaryDataForHeader.numberOfRatings,
                                  })}
                        </Typography>
                    </Box>
                    {currentUser?.id !== user?.id && !company && (
                        <Button
                            variant="outlined"
                            startIcon={<Icon size="1.5rem" name="StarBlank" />}
                            onClick={() => createAuthenticatedHandler(handleOpenRatingModal)}
                        >
                            {t('profile:addRating')}
                        </Button>
                    )}
                    <StyledDialog onClose={handleCloseRatingModal} open={ratingModalOpened}>
                        <Formik validationSchema={schema} initialValues={{}} onSubmit={handleSubmit}>
                            {({ values, setFieldValue }) => {
                                const handleRatingChange = (_event: React.SyntheticEvent, value: number | null) => {
                                    setFieldValue('rating', value);
                                };

                                const ratingToShowText = hoveredRating > 0 ? hoveredRating : values.rating;

                                return (
                                    <Form>
                                        <DialogTitle>
                                            <Typography variant="h2" variantMapping={{ h2: 'span' }}>
                                                {t('profile:addRatingModalTitle')}
                                            </Typography>
                                        </DialogTitle>
                                        <DialogContent>
                                            <Box display="flex" flexDirection="column" alignItems="center">
                                                <Box mb={2}>
                                                    <StyledAvatar
                                                        alt={user?.fullName ?? company?.name}
                                                        image={(user?.image ?? company?.image) as Medium}
                                                        fallbackText={user?.initials ?? company?.initials}
                                                        size={64}
                                                    />
                                                </Box>
                                                <Typography variant="h3" paragraph>
                                                    {user?.fullName ?? company?.name}
                                                </Typography>
                                                <Box my={1} display="flex" flexDirection="column" alignItems="center">
                                                    <Typography gutterBottom>
                                                        {ratingToShowText
                                                            ? t(`profile:rating${ratingToShowText}`)
                                                            : t('profile:selectRating')}
                                                    </Typography>
                                                    <StyledRating
                                                        onChange={handleRatingChange}
                                                        onChangeActive={handleRatingHover}
                                                    />
                                                </Box>
                                                <TextField
                                                    name="comment"
                                                    placeholder={t('profile:comment')}
                                                    minRows={4}
                                                    multiline
                                                />
                                            </Box>
                                        </DialogContent>
                                        <StyledDialogActions>
                                            <Button variant="outlined" onClick={handleCloseRatingModal}>
                                                {t('common:cancel')}
                                            </Button>
                                            <Button type="submit" disabled={!values.rating}>
                                                {t('profile:publish')}
                                            </Button>
                                        </StyledDialogActions>
                                    </Form>
                                );
                            }}
                        </Formik>
                        <CloseButton aria-label="close" onClick={handleCloseRatingModal}>
                            <Icon name="Close" />
                        </CloseButton>
                    </StyledDialog>
                </Box>
                <Box my={4}>
                    {mergedRatings.length > 0 ? (
                        <Grid spacing={4} container>
                            {mergedRatings.map(
                                (rating) =>
                                    rating && (
                                        <Grid key={rating.id} sm={6} item>
                                            <RatingCard rating={rating} />
                                        </Grid>
                                    ),
                            )}
                        </Grid>
                    ) : (
                        <Typography variant="h3">{t('profile:noRatings')}</Typography>
                    )}
                </Box>
                {mergedRatings.length < (user?.numberOfDisplayedRatings ?? company?.numberOfDisplayedRatings ?? 0) && (
                    <Button onClick={handleLoadMore} variant="outlined">
                        {t('profile:loadMoreRatings')}
                    </Button>
                )}
            </StyledCardContent>
        </StyledCard>
    );
};

export default RatingSection;
